import { useOnboarding } from "@onboardjs/react";
import { ArrowLeft, Check } from "lucide-react";
import { Button } from "../../components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "../../components/ui/card";

const STEP_ORDER = [
	"domain-discovery",
	"connect-email",
	"review-firm",
	"person-profile",
];

const STEP_LABELS: Record<string, string> = {
	"domain-discovery": "Company",
	"connect-email": "Email",
	"review-firm": "Firm Profile",
	"person-profile": "Your Profile",
};

export function SetupShell({ children }: { children: React.ReactNode }) {
	const { state, previous, isLoading } = useOnboarding();

	const currentStepId = (state?.currentStep?.id ?? undefined) as
		| string
		| undefined;
	const currentIndex = currentStepId ? STEP_ORDER.indexOf(currentStepId) : -1;

	const payload = (state?.currentStep?.payload ?? undefined) as
		| { title?: string; description?: string }
		| undefined;

	return (
		<div className="flex min-h-screen flex-col bg-background">
			<header className="border-b px-6 py-4">
				<div className="mx-auto flex max-w-3xl items-center justify-between">
					<div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
						Clerical Setup
					</div>
					<nav className="flex items-center gap-2">
						{STEP_ORDER.map((stepId, index) => {
							const isActive = stepId === currentStepId;
							const isCompleted = index < currentIndex;
							return (
								<div key={stepId} className="flex items-center gap-2">
									{index > 0 && (
										<div
											className={`h-px w-6 ${isCompleted ? "bg-primary" : "bg-border"}`}
										/>
									)}
									<div
										className={`flex h-7 items-center gap-1.5 rounded-full px-3 text-xs font-medium transition-colors ${
											isActive
												? "bg-primary text-primary-foreground"
												: isCompleted
													? "bg-primary/10 text-primary"
													: "bg-muted text-muted-foreground"
										}`}
									>
										{isCompleted && <Check className="h-3 w-3" />}
										{STEP_LABELS[stepId] ?? stepId}
									</div>
								</div>
							);
						})}
					</nav>
				</div>
			</header>

			<main className="flex flex-1 items-start justify-center px-4 py-8">
				<Card className="w-full max-w-3xl shadow-sm">
					<CardHeader>
						{payload?.title && <CardTitle>{payload.title}</CardTitle>}
						{payload?.description && (
							<CardDescription>{payload.description}</CardDescription>
						)}
					</CardHeader>
					<CardContent>{children}</CardContent>
					<div className="flex items-center justify-between border-t px-6 py-4">
						<div>
							{state?.canGoPrevious && (
								<Button
									variant="ghost"
									onClick={() => previous()}
									disabled={isLoading}
								>
									<ArrowLeft className="mr-2 h-4 w-4" />
									Back
								</Button>
							)}
						</div>
						<div className="text-xs text-muted-foreground">
							Step {currentIndex + 1} of {STEP_ORDER.length}
						</div>
					</div>
				</Card>
			</main>
		</div>
	);
}
